import {
  View,
  createPlugin,
  memoizeRendering,
  createElement
} from "@fullcalendar/core";
import _ from "lodash";
import moment from "moment";

class ScheduleView extends View {
  renderSkeleton = memoizeRendering(
    this._renderSkeleton,
    this._unrenderSkeleton
  );

  initialize() {
    // called once when the view is instantiated, when the user switches to the view.
    // initialize member variables or do other setup tasks.
  }

  render(props) {
    super.render(props);

    this.renderSkeleton(this.context);
    this.renderScheduleEvents();
  }

  destroy() {
    super.destroy();

    this.renderSkeleton.unrender();
  }

  _renderSkeleton(context) {
    // responsible for displaying the skeleton of the view within the already-defined
    // this.el, an HTML element
    this.el.innerHTML = this.renderSkeletonHtml();
    this.el.classList.add("fc-schedule-view");
    this.el.querySelector(".tab-date").addEventListener("click", () => {
      context.calendar.changeView("scheduleByDate");
    });
    this.el.querySelector(".tab-calendar").addEventListener("click", () => {
      context.calendar.changeView("scheduleByCalendar");
    });
    this.contentsEl = this.el.querySelector(".fc-schedule-contents");
  }

  _unrenderSkeleton() {
    this.el.classList.remove("fc-schedule-view");
  }

  renderSkeletonHtml() {
    return `
    <div class="fc-schedule-tabs">
      <span class="label">Group By</span>
      <span class="tab tab-date">Date</span>
      <span class="tab tab-calendar">Calendar</span>
    </div>
    <div class="fc-schedule-contents">
    </div>
    `;
  }

  renderScheduleEvents() {
    this.el.querySelector(".fc-schedule-contents").innerHTML = "";

    switch (this.context.options.groupBy) {
      case "date":
        this.renderDateGroups();
        break;
      case "calendar":
        this.renderCalendarGroups();
        break;
      default:
        break;
    }
  }

  renderCalendarGroups() {
    const groups = this.groupEventsByCalendar();

    groups.forEach(g => {
      this.renderGroupHeader(g.name, g.color);
      this.renderCalendarEvents(g.events);
    });
  }

  renderDateGroups() {
    const groups = this.groupEventsByDate();

    groups.forEach(g => {
      this.renderGroupHeader(g.name);
      this.renderDateEvents(g.events);
    });
  }

  getEventsArray(groupBy) {
    const store = this.calendar.renderableEventStore;
    let events = Object.values(store.defs).map(ev => {
      const instanceId = parseInt(ev.defId) + 1;
      const instance = store.instances[instanceId];
      const formattedTime = this.formatEventTime(instance.range);

      return {
        title: ev.title,
        time: formattedTime,
        calendar: ev.extendedProps.calendarName,
        date: moment.utc(instance.range.start).format("dddd, MMMM DD, YYYY"),
        rawDate: instance.range.start,
        color: ev.ui.backgroundColor,
        startTs: instance.range.start.getTime(),
        endTs: instance.range.end.getTime()
      };
    });

    if (groupBy === "date") {
      const startTs = this.currentStart.getTime();
      const endTs = this.currentEnd.getTime() + 30 * 86400000;
      events = events.filter(ev => ev.startTs < endTs && ev.endTs >= startTs);
    } else if (groupBy === "calendar") {
      const year = this.currentStart.getFullYear();
      const month = this.currentStart.getMonth();

      events = events.filter(
        ev =>
          ev.rawDate.getFullYear() === year && ev.rawDate.getMonth() === month
      );
    }

    return events;
  }

  groupEventsByCalendar() {
    const groupedEvents = _.groupBy(
      this.getEventsArray("calendar"),
      "calendar"
    );
    const result = [];
    this.context.options.calendars.forEach(c => {
      if (groupedEvents[c.name]) {
        result.push({
          name: c.name,
          color: c.color,
          events: groupedEvents[c.name]
        });
      }
    });

    return result;
  }

  groupEventsByDate() {
    const groupedEvents = _.groupBy(this.getEventsArray("date"), "date");
    const result = Object.values(groupedEvents).map(group => ({
      name: group[0].date,
      events: group
    }));

    return result;
  }

  renderGroupHeader(name, color) {
    const colorDiv = color
      ? `<div class="group-color" style="background-color: ${color};"></div>`
      : "";
    const el = createElement(
      "div",
      { className: "group-header" },
      `
        <div>
          ${colorDiv}
          <div class="group-name">${name}</div>
        </div>
        <i class="material-icons">keyboard_arrow_down</i>
      `
    );

    el.querySelector(".material-icons").addEventListener("click", function(ev) {
      const containerEl = ev.target.parentElement.nextSibling;
      if (containerEl.className.indexOf("closed") >= 0) {
        containerEl.classList.remove("closed");
      } else {
        containerEl.classList.add("closed");
      }
      if (ev.target.innerText === "keyboard_arrow_down") {
        ev.target.innerText = "keyboard_arrow_up";
      } else {
        ev.target.innerText = "keyboard_arrow_down";
      }
    });

    this.contentsEl.append(el);
  }

  renderCalendarEvents(events) {
    const eventHtmls = events.map(
      ev => `
      <div class="event">
        <div class="title-time">
          <div>${ev.title}</div>
          <div>${ev.time}</div>
        </div>
      </div>
    `
    );
    const el = createElement(
      "div",
      { className: "events-container" },
      eventHtmls.join("")
    );
    this.contentsEl.append(el);
  }

  renderDateEvents(events) {
    const eventHtmls = events.map(ev => {
      const colorStyle = ev.color
        ? ` style="background-color: ${ev.color};"`
        : "";

      return `
        <div class="event">
          <div class="title-time">
            <div>${ev.title}</div>
            <div>${ev.time}</div>
          </div>
          <div class="calendar">
            <div class="color-circle"${colorStyle}></div>
            <div>${ev.calendar}</div>
          </div>
        </div>
      `;
    });
    const el = createElement(
      "div",
      { className: "events-container" },
      eventHtmls.join("")
    );
    this.contentsEl.append(el);
  }

  formatEventTime(range) {
    const start = moment.utc(range.start);
    const startDate = start.format("MMM DD, YYYY");
    const startTime = start.format("hh:mma");
    const end = moment.utc(range.end);
    const endDate = end.format("MMM DD, YYYY");
    const endTime = end.format("hh:mma");

    if (startDate === endDate) {
      return `${startDate}&nbsp;&nbsp;&nbsp;${startTime}&nbsp;&nbsp;&nbsp;–&nbsp;&nbsp;&nbsp;${endTime}`;
    }

    return `${startDate}&nbsp;&nbsp;&nbsp;${startTime}&nbsp;&nbsp;&nbsp;–&nbsp;&nbsp;&nbsp;${endDate}&nbsp;&nbsp;&nbsp;${endTime}`;
  }
}

export default createPlugin({
  defaultView: "scheduleByDate",
  views: {
    schedule: {
      class: ScheduleView
    },
    scheduleByDate: {
      type: "schedule",
      groupBy: "date"
    },
    scheduleByCalendar: {
      type: "schedule",
      groupBy: "calendar"
    }
  }
});
